import { useTranslation } from 'react-i18next';
/**
 * ContractsCrossRefPanel.js
 *
 * Drop-in panel that lists contracts linked to a given employee /
 * customer / project. Used as a "Contracts" sub-section on the
 * Employee, Customer and Project detail pages so a manager can see
 * every contract that touches the entity at a glance.
 *
 * Two listing modes share the same UI:
 *   <ContractsCrossRefPanel scope="employee" id={employee.id} />
 *   <ContractsCrossRefPanel scope="customer" id={customer.id} />
 *   <ContractsCrossRefPanel scope="project"  id={project.id}  />
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Download, FileSignature, ExternalLink } from 'lucide-react';
import { filenameFromContentDisposition } from './contracts/_shared';
import { SearchSortBar, CREATED_SORT_OPTIONS, matchesQuery, matchesWeek, byCreated, byText } from './ListSearchSort';
import { useSavedView } from '../hooks/useSavedView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-700',
  in_review: 'bg-amber-100 text-amber-700',
  signed: 'bg-green-100 text-green-700',
  terminated: 'bg-red-100 text-red-700',
  archived: 'bg-gray-100 text-gray-500',
};
const KIND_LABEL = {
  master: 'Master', annex: 'Annex',
  support_letter: 'Support Letter', client_letter: 'Client Letter',
};

export default function ContractsCrossRefPanel({ scope, id, onOpenContract }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { search, setSearch, sortBy, setSortBy } = useSavedView(`contracts-xref-${scope}`);
  const [week, setWeek] = useState(null);

  const DOC_SORTS = {
    created_desc: byCreated('desc'),
    created_asc: byCreated('asc'),
    number: byText((c) => c.contract_number),
    title: byText((c) => c.title),
    kind: byText((c) => KIND_LABEL[c.kind] || c.kind),
    status: byText((c) => c.status),
    sign_date: (a, b) => String(b.sign_date || '').localeCompare(String(a.sign_date || '')),
  };
  const visible = useMemo(() => {
    const list = items.filter((c) => matchesWeek(week, c.sign_date || c.created_at) && matchesQuery(search, [
      c.contract_number, c.title, KIND_LABEL[c.kind] || c.kind,
      c.status, c.sign_date, c.employee_name, c.customer_name, c.project_name,
    ]));
    return [...list].sort(DOC_SORTS[sortBy] || DOC_SORTS.created_desc);
    /* eslint-disable-next-line */
  }, [items, search, sortBy, week]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const param = { employee: 'employee_id', customer: 'customer_id', project: 'project_id' }[scope];
    axios.get(`${API}/contracts`, { params: { [param]: id } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [scope, id]);

  const downloadPdf = async (cid, number) => {
    try {
      const r = await axios.get(`${API}/contracts/${cid}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFromContentDisposition(
        r.headers['content-disposition'],
        `Document-${(number || cid).replace(/\//g, '-')}.pdf`,
      );
      a.click(); window.URL.revokeObjectURL(url);
    } catch { /* swallow */ }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 italic py-2">{t('auto.misc2.loadingContracts')}</div>;
  }
  if (items.length === 0) {
    return (
      <div className="text-xs text-slate-400 italic py-3 px-2 border rounded bg-slate-50/50 flex items-center gap-2">
        <FileSignature className="h-3.5 w-3.5" />
        No documents linked to this {scope}.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={`contracts-xref-${scope}`}>
      <SearchSortBar
        search={search}
        onSearch={setSearch}
        sortBy={sortBy}
        onSort={setSortBy}
        placeholder={t('auto.misc2.searchByNumberTitleKindStatus')}
        testIdPrefix={`contracts-xref-${scope}`}
        week={week}
        onWeek={setWeek}
        options={[
          ...CREATED_SORT_OPTIONS,
          { value: 'number', label: t('auto.lbl.documentNumber') },
          { value: 'title', label: t('auto.lbl.title') },
          { value: 'kind', label: t('auto.lbl.kind') },
          { value: 'status', label: t('auto.lbl.status') },
          { value: 'sign_date', label: t('auto.lbl.signDate') },
        ]}
      />
      {visible.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 px-2 border rounded bg-slate-50/50">
          No documents match “{search}”.
        </div>
      ) : (
      <div className="border rounded overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-2 py-1.5">{t('auto.misc2.number')}</th>
            <th className="text-left px-2 py-1.5">{t('auto.misc2.title')}</th>
            <th className="text-left px-2 py-1.5 w-20">{t('auto.misc2.kind')}</th>
            <th className="text-left px-2 py-1.5 w-24">{t('auto.misc2.status')}</th>
            <th className="text-left px-2 py-1.5 w-24">{t('auto.misc2.signDate')}</th>
            <th className="text-right px-2 py-1.5 w-20"></th>
          </tr>
        </thead>
        <tbody>
          {visible.map((c) => (
            <tr key={c.id} className="border-t hover:bg-slate-50">
              <td className="px-2 py-1.5 font-mono">{c.contract_number}</td>
              <td className="px-2 py-1.5 truncate max-w-[200px]">{c.title || '—'}</td>
              <td className="px-2 py-1.5">
                <Badge variant="outline" className="text-[10px]">{KIND_LABEL[c.kind] || c.kind}</Badge>
              </td>
              <td className="px-2 py-1.5">
                <Badge className={`${STATUS_BADGE[c.status]} text-[10px]`}>
                  {(c.status || '').replace(/_/g, ' ')}
                </Badge>
              </td>
              <td className="px-2 py-1.5 font-mono">{c.sign_date || '—'}</td>
              <td className="px-2 py-1.5 text-right whitespace-nowrap">
                {onOpenContract && (
                  <Button size="icon" variant="ghost" className="h-6 w-6"
                    onClick={() => onOpenContract(c.id)} title={t('auto.misc2.openContract')}>
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-6 w-6"
                  onClick={() => downloadPdf(c.id, c.contract_number)} title={t('auto.misc2.downloadPdf')}>
                  <Download className="h-3 w-3 text-blue-600" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}
    </div>
  );
}
